<template>
  <div class="flex">
    <DropdownModule
      ref="dropdownRef"
      :icon="isMobile ? 'icon-more text-22' : 'icon-more-horizontal text-24 mt-2'"
      class="flex"
      :width="220"
      append-to-body
      position-menu="right"
      :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
    >
      <div class="rounded-10 text-14">
        <div v-if="!isSharePage" class="rounded-t-10">
          <div class="rounded-t-10 hover:bg-selection group-scope">
            <ButtonModule
              text="Change design"
              :prefix="`icon-design mr-14 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 pt-16 pb-9 w-full"
              @click="changeDesign"
            />
          </div>
        </div>
        <div class="border-b border-hover">
          <div class="hover:bg-selection group-scope" :class="{'pt-10 pb-6 rounded-t-10': isSharePage}">
            <ButtonModule
              text="Send to mail"
              :prefix="`icon-email text-14 mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="send"
            />
          </div>
          <ProOnly class="group-scope" :class="{'hover:bg-selection ': !isFreePlan}">
            <ButtonModule
              text="Export"
              :disabled="isFreePlan"
              :prefix="`icon-export mr-16 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color pl-20 py-9"
              :class="{'w-full': !isFreePlan}"
              @click="toggleExport"
            />
          </ProOnly>

          <div v-if="allowWrite" class="hover:bg-selection group-scope">
            <ButtonModule
              :text="currentProject.isLocked ? 'Unlock' : 'Lock'"
              class="text-app-color px-20 py-9 w-full"
              :prefix="`${currentProject.isLocked ? 'icon-lock-close mr-14' : 'icon-lock-open mr-14'} ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              @click="lock"
            />
          </div>

          <div class="hover:bg-selection group-scope">
            <ButtonModule
              text="Print"
              :prefix="`icon-print text-17 mr-12 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="print"
            />
          </div>
          <div v-if="!isSharePage" class="hover:bg-selection group-scope">
            <ButtonModule
              text="Delete"
              :prefix="`icon-delete text-17 mr-12 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="onDeleteProject"
            />
          </div>
        </div>

        <div v-if="!isSharePage" class="hover:bg-selection group-scope border-b border-hover">
          <ButtonModule
            text="Save as template"
            :prefix="`icon-note-add text-17 mr-12 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
            class="text-app-color px-20 py-9 w-full"
            @click="openTemplateModal"
          />
        </div>
        <div class="px-20 py-12 text-app-color text-13 opacity-80" @click.stop>
          <p>Word count: <span class="font-bold">{{ words }}</span></p>
          <p>File size: <span class="font-bold">{{ fileSize }}</span></p>
          <p class="pr-10">Auto-saved: <span class="font-bold">{{ parseDate(currentProject.updatedAt, 'lastUpdate') }}</span></p>
        </div>
      </div>

      <ButtonModule
        :has-hover="true"
        prefix="icon-info_outline text-main-color"
        class="text-app-color absolute bottom-0 right-0 px-10 py-16 text-19"
        @click="openNoteInfo"
      />
    </DropdownModule>
    <iframe v-if="currentProject && settings" ref="iframeRef" class="w-0 h-0 opacity-0" :srcdoc="html()" />

    <SendModal ref="sendModalRef" :project="currentProject" />
    <ExportModal ref="exportModalRef" />
    <NoteInfo ref="noteInfoRef" :project="currentProject" :size="fileSize" :words="words" />
    <NewTemplateModal
      ref="templateRef"
      :image="templateImage"
      :blob="templateBlob"
      :content="currentProject.body"
    />
    <HeaderDesign ref="headerDesignRef" />
  </div>
</template>

<script>
import ProOnly from '@/components/shared/ProOnly'
import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ExportModal from '@/components/confirm/ExportModal'
import SendModal from '@/components/confirm/SendModal'
import NoteInfo from '@/components/confirm/NoteInfo'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import NewTemplateModal from '@/components/header/NewTemplateModal'
import HeaderDesign from '@/components/header/HeaderDesign'

import html2canvas from 'html2canvas'

import { generateHTML } from '@/core/helper-functions'
import designConfig from '@/core/design-config'

import { defineComponent, computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { parseDate } from '@/plugins/filters'

export default defineComponent({
  components: { ProOnly, DropdownModule, ButtonModule, ExportModal, SendModal, NoteInfo, NewTemplateModal, HeaderDesign },

  setup () {
    const { state, getters, dispatch, commit } = useStore()
    const route = useRoute()

    const templateImage = ref(null)
    const templateBlob = ref(null)

    const headerDesignRef = ref(null)
    const dropdownRef = ref(null)
    const exportModalRef = ref(null)
    const sendModalRef = ref(null)
    const iframeRef = ref(null)
    const noteInfoRef = ref(null)
    const templateRef = ref(null)

    const currentProject = computed(() => state.project.currentProject)
    const showSidebar = computed(() => state.settings.showSidebar)
    const settings = computed(() => state.settings.settings)
    const words = computed(() => state.project.words)
    const user = computed(() => state.profile.user)
    const size = computed(() => state.project.size)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isSharePage = computed(() => route.name === 'Share')
    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isFreePlan = computed(() => getters.isFreePlan)

    const allowWrite = computed(() => isSharePage.value ? state.project.share.mode === 'write' : true)

    const fileSize = computed(() => {
      if (size.value / 1024 / 1024 < 1) return `${(size.value / 1024).toFixed(0)}kb`
      return `${(size.value / 1024 / 1024).toFixed(2)}Mb`
    })

    function close () {
      dropdownRef.value.close()
    }

    function toggleExport () {
      if (!isFreePlan.value) {
        exportModalRef.value.open()
        close()
      }
    }

    function send () {
      sendModalRef.value.open()
      close()
    }

    function changeDesign () {
      setTimeout(() => {
        close()

        if (isMobile.value) {
          commit('changeActiveState', 'Design')
          commit('toggleSettingsModal', true)
          commit('toggleAside', false)
          commit('updateBackButton', false)
        } else {
          headerDesignRef.value.open()
        }
      }, 10)
    }

    function onDeleteProject () {
      close()
      dispatch('deleteProject', currentProject.value.id)
    }

    function html () {
      return generateHTML(currentProject.value.body, settings.value, currentProject.value.title)
    }

    function print () {
      const frame = iframeRef.value
      frame.contentWindow.print()
    }

    function openNoteInfo () {
      noteInfoRef.value.open()
    }

    function openTemplateModal () {
      commit('toggleBookLoading', true)
      const element = document.querySelector('#editor-wrapper')
      const maxWidth = designConfig.columns[settings.value.columnWidth]

      const config = {
        backgroundColor: designConfig.themes[settings.value.theme].canvasBg,
        proxy: `${process.env.VUE_APP_API}/api/attachments/proxy`,
        height: isMobile.value ? 300 : maxWidth
      }

      html2canvas(element, config)
        .then(canvas => {
          commit('toggleBookLoading', false)
          templateImage.value = canvas.toDataURL()
          canvas.toBlob(blob => (templateBlob.value = blob))
          templateRef.value.open()
        })
        .catch(() => commit('toggleBookLoading', false))
    }

    function lock () {
      commit('loading', true)

      dispatch('editProject', {
        id: currentProject.value.id,
        data: {
          ...currentProject.value,
          isLocked: !currentProject.value.isLocked
        }
      }).then(() => {
        commit('setCurrentProject', {
          ...currentProject.value,
          isLocked: !currentProject.value.isLocked
        })
      })
    }

    function upgradeToPro () {
      if (user.value) {
        window.open(`https://helloivy.co/upgrade?userId=${user.value.id}`, '_blank')
      }
    }

    return {
      templateImage,
      templateBlob,
      // refs
      headerDesignRef,
      dropdownRef,
      exportModalRef,
      sendModalRef,
      iframeRef,
      noteInfoRef,
      templateRef,
      // computeds
      currentProject,
      showSidebar,
      settings,
      words,
      isMobile,
      isDarkMode,
      isTwilightTheme,
      isSharePage,
      fileSize,
      allowWrite,
      isFreePlan,
      // functions
      toggleExport,
      send,
      changeDesign,
      onDeleteProject,
      html,
      print,
      openNoteInfo,
      lock,
      openTemplateModal,
      parseDate,
      upgradeToPro
    }
  }
})
</script>
